// Impact assessment marks on the canvas: which objects are compromised, and which are affected
// because something beneath them is.
//
// Held OUTSIDE the nodes array, like connectionValidity and focusMode: the marks change every
// time an engineer clicks one, and writing them into node.data would re-run the layout and
// re-render every node. Each node's mark subscribes here instead, so a click repaints the marks
// and nothing else. It also means the canvas itself needs no "assessment mode": a page that wants
// marks sets them, and a canvas with none set draws none.

let state = { active: false, compromised: new Set(), affected: new Set(), canMark: false, onToggle: null };
const listeners = new Set();
let version = 0;

function emit() {
  version += 1;
  listeners.forEach((listener) => listener());
}

export function subscribeImpactMarks(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getImpactMarksVersion() {
  return version;
}

/**
 * Show marks on the canvas.
 * @param {{ compromised: Iterable<string>, affected: Iterable<string>, canMark?: boolean,
 *           onToggle?: (objectId: string, nowCompromised: boolean) => void }} marks
 */
export function setImpactMarks({ compromised, affected, canMark = false, onToggle = null }) {
  state = { active: true, compromised: new Set(compromised || []), affected: new Set(affected || []), canMark, onToggle };
  emit();
}

/** Stop showing marks (the assessment page has closed). */
export function clearImpactMarks() {
  if (!state.active) return;
  state = { active: false, compromised: new Set(), affected: new Set(), canMark: false, onToggle: null };
  emit();
}

/**
 * What to draw for one object: null when no assessment is showing; otherwise its state -
 * 'compromised', 'affected' (something it depends on is compromised) or 'clear' - and whether
 * the viewer may change it.
 */
export function impactStateFor(objectId) {
  if (!state.active) return null;
  const id = String(objectId ?? '');
  const mark = state.compromised.has(id) ? 'compromised' : state.affected.has(id) ? 'affected' : 'clear';
  return { mark, canMark: state.canMark && typeof state.onToggle === 'function' };
}

export function toggleImpactMark(objectId) {
  if (!state.active || !state.canMark || typeof state.onToggle !== 'function') return;
  const id = String(objectId ?? '');
  state.onToggle(id, !state.compromised.has(id));
}
